import React from 'react';
import LOGO from '../../IMG/logo192.jpg'
import { 
    MapPinIcon, 
    PhoneIcon, 
    EnvelopeIcon,
    BuildingOffice2Icon,
    HeartIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';

/**
 * Footer component for Ratchaphruek Hemodialysis
 * Modern, SEO-optimized design with trust indicators
 */
const Footer = () => {
    const currentYear = new Date().getFullYear();
    
    const footerLinks = [
        { name: 'หน้าแรก', href: '/', description: 'กลับสู่หน้าหลัก' },
        { name: 'หน่วยไตเทียม', href: '/hemodialysisUnit', description: 'ศูนย์ไตเทียมทั่วประเทศ' },
        { name: 'เกี่ยวกับเรา', href: '/about', description: 'ประวัติและวิสัยทัศน์' },
        { name: 'นโยบายความเป็นส่วนตัว', href: '/privacyPolicy', description: 'ข้อมูลส่วนบุคคล' },
        { name: 'ข้อกำหนดการใช้งาน', href: '/termsOfUse', description: 'เงื่อนไขการใช้บริการ' },
    ];

    const trustBadges = [
        { icon: ShieldCheckIcon, text: 'มาตรฐาน HA', color: 'text-blue-600' },
        { icon: HeartIcon, text: 'ดูแลด้วยใจ', color: 'text-orange-600' },
        { icon: BuildingOffice2Icon, text: '70+ สาขา', color: 'text-emerald-600' },
    ];

    return (
        <footer className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
            </div>

            <div className="relative mx-auto max-w-7xl px-4 pt-16 pb-8 sm:px-6 lg:px-8">
                {/* Trust Badges Section */}
                <div className="mb-12 flex flex-wrap justify-center gap-6">
                    {trustBadges.map((badge, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                            <badge.icon className={`w-6 h-6 ${badge.color}`} />
                            <span className="text-sm font-bold">{badge.text}</span>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
                    
                    {/* Brand Section - Enhanced */}
                    <div className="col-span-1 md:col-span-1">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="relative">
                                <img 
                                    alt="ราชพฤกษ์ไตเทียม - ศูนย์ฟอกเลือดมาตรฐาน HA" 
                                    src={LOGO} 
                                    className="h-12 w-auto rounded-2xl shadow-xl shadow-blue-500/50 transition-transform hover:scale-105" 
                                />
                                <div className="absolute -bottom-1 -right-1 h-4 w-4 bg-orange-500 rounded-full border-2 border-slate-900 animate-pulse"></div>
                            </div>
                            <span className="text-xl font-black tracking-tight">ราชพฤกษ์ไตเทียม</span>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed mb-6">
                            <strong className="text-white">ผู้นำด้านการฟอกเลือดด้วยเครื่องไตเทียม</strong> ที่ได้มาตรฐาน HA 
                            ด้วยประสบการณ์กว่า <span className="text-orange-400 font-bold">20 ปี</span> และเครือข่ายศูนย์ไตเทียม 
                            <span className="text-blue-400 font-bold"> 70+ แห่ง</span> ทั่วประเทศไทย
                        </p>
                        
                        {/* Social Media - Modern Icons */}
                        <div className="flex gap-3">
                            <a 
                                href="https://www.facebook.com/ratchapruekhd" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="group w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-slate-300 hover:bg-blue-600 hover:border-blue-500 hover:text-white hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300"
                                aria-label="Facebook ราชพฤกษ์ไตเทียม"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                                </svg>
                            </a>
                            <a 
                                href="tel:0811506992" 
                                className="group w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-slate-300 hover:bg-emerald-600 hover:border-emerald-500 hover:text-white hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300"
                                aria-label="โทรศัพท์ติดต่อ"
                            >
                                <PhoneIcon className="w-5 h-5" />
                            </a>
                            <a 
                                href="mailto:info@ratchaphruek.com" 
                                className="group w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-slate-300 hover:bg-orange-600 hover:border-orange-500 hover:text-white hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300"
                                aria-label="อีเมลติดต่อ"
                            >
                                <EnvelopeIcon className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    {/* Quick Links - Enhanced */}
                    <div className="col-span-1">
                        <h3 className="text-sm font-black text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                            <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-orange-500 rounded-full"></div>
                            เมนูทางลัด
                        </h3>
                        <ul className="space-y-3">
                            {footerLinks.map((link) => (
                                <li key={link.name}>
                                    <a 
                                        href={link.href} 
                                        className="group flex items-start gap-2 text-slate-300 hover:text-white text-sm font-medium transition-all duration-300"
                                    >
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-500 group-hover:bg-orange-500 group-hover:scale-150 transition-all"></span>
                                        <div>
                                            <div className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</div>
                                            <div className="text-xs text-slate-500 group-hover:text-slate-400">{link.description}</div>
                                        </div>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info - Enhanced */}
                    <div className="col-span-1">
                        <h3 className="text-sm font-black text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                            <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-blue-500 rounded-full"></div>
                            ติดต่อสอบถาม
                        </h3>
                        <div className="space-y-5 text-sm text-slate-300">
                            <div className="group flex items-start gap-3 p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                                <MapPinIcon className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                <div>
                                    <p className="font-bold text-white mb-1">สำนักงานใหญ่</p>
                                    <p className="text-xs leading-relaxed">
                                        ถนนจรัญสนิทวงศ์ ซอยจรัญสนิทวงศ์27 แยก 7<br />
                                        แขวงบ้านช่างหล่อ เขตบางกอกน้อย<br />
                                        กรุงเทพมหานคร 10700
                                    </p>
                                </div>
                            </div>
                            
                            <div className="group flex items-center gap-3 p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                                <PhoneIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                <div>
                                    <p className="font-bold text-white mb-1">โทรศัพท์</p>
                                    <a href="tel:0811506992" className="text-emerald-400 hover:text-emerald-300 font-semibold">
                                        081-150-6992
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar - Enhanced */}
                <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-slate-400 text-xs text-center md:text-left">
                        <p className="font-medium">
                            © {currentYear} <span className="text-white font-bold">ราชพฤกษ์ไตเทียม</span>. All Rights Reserved.
                        </p>
                        <span className="hidden md:inline text-slate-600">|</span>
                        <p className="text-slate-500">
                            Designed with <HeartIcon className="inline w-3 h-3 text-orange-500 animate-pulse" /> for better healthcare
                        </p>
                    </div>
                    <div className="flex gap-6 text-xs font-medium text-slate-400">
                        <a href="/privacyPolicy" className="hover:text-white transition-colors duration-300">
                            นโยบายความเป็นส่วนตัว
                        </a>
                        <a href="/termsOfUse" className="hover:text-white transition-colors duration-300">
                            ข้อกำหนดการใช้งาน
                        </a>
                    </div>
                </div>

                {/* SEO Keywords Section (Hidden but crawlable) */}
                <div className="sr-only">
                    <h2>ศูนย์ฟอกเลือดด้วยเครื่องไตเทียม ราชพฤกษ์</h2>
                    <p>
                        บริการฟอกเลือด HD HDF ไตเทียม โรงพยาบาล คลินิกไตเทียม ทั่วประเทศไทย 
                        มาตรฐาน HA รักษาโรคไต ไตวาย ไตเสื่อม ศูนย์ไตเทียม hemodialysis dialysis center
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
